import { z } from 'zod';
import { hrRoleSchema } from '../schemas/auth.js';
import { gateSchema, requirementKeySchema } from '../requirements/index.js';
/**
 * The readiness vocabularies (ADR-006 §9-§10). Code, never configuration.
 *
 * The four states and their precedence read like policy, which is exactly why they must
 * not be configurable: making precedence a setting lets "Ready" mean different things on
 * different days, untestably — the single piece of flexibility that would destroy the
 * guarantee configurability exists to provide.
 */
/**
 * Decision 9's states. snake_case, matching `stepStateSchema`.
 *
 * There is no state for "waived": an exception lifts a gate, it never satisfies a
 * requirement, so a waived person is `ready_with_exceptions` with the gap still visible.
 *
 * `setup_required` was added on 2026-09-10 to close a false positive that reached staging
 * (docs/mvp/known-limitations.md #41). A person with no job profile resolves to an empty
 * requirement set; the fold found nothing unmet and answered `ready`, so the board showed
 * "No role set · Ready · Nothing on HR's desk". **An empty requirement set is not evidence
 * of compliance — it is the absence of a statement about what this person needs.** The two
 * situations must stay distinguishable: nobody has said what this person needs, versus
 * everything they need is in place.
 *
 * It is a fifth *verdict*, not a fifth kind of thing. "Still loading" and "could not be
 * derived" are deliberately NOT states here: they are properties of a request, not of a
 * person, and the UI represents them separately (`usePeopleReadiness`). Folding them in
 * would make a network hiccup indistinguishable from a compliance fact.
 */
export const readinessStateSchema = z.enum([
    'ready',
    'not_ready',
    'ready_with_exceptions',
    'needs_review',
    'setup_required',
]);
/**
 * Why a person landed in `needs_review`. A closed union, because "needs review" with no
 * stated cause is an instruction to go and stare at a record until something occurs to
 * you.
 *
 * The operational distinction this encodes: NOT READY means we know what is missing and
 * who owes it — chase the person. NEEDS REVIEW means the ball is on HR's desk, or the
 * data cannot be trusted — a human must look.
 */
export const reviewCauseSchema = z.enum([
    /** A submitted step sitting in the review queue. */
    'awaiting_approval',
    /** Two live credentials for one key with different expiries, or similar. */
    'conflicting_evidence',
    /** expiresAt <= issuedAt, or a missing date the kind requires. */
    'impossible_dates',
    /** The rule's interval is not settled — an `unresolved` expiry carrying its question id. */
    'unresolvable_rule',
    /** A conditional gating requirement whose trigger input is unanswered. */
    'missing_trigger_input',
]);
/**
 * A recorded override — per requirement AND per gate, never per person, because "this
 * candidate is fine" is exactly the statement an auditor cannot check.
 *
 * Time-boxing costs nothing to enforce: when the exception lapses the person flips back
 * to Not ready automatically, with no cleanup job, because state is derived. That is the
 * cleanest argument for derivation in the whole system.
 */
export const requirementExceptionSchema = z.object({
    id: z.string().min(1),
    docType: z.literal('requirement-exception'),
    personId: z.string().min(1),
    requirementKey: requirementKeySchema,
    gate: gateSchema,
    /** Long enough to be a reason rather than a shrug. */
    reason: z.string().min(20),
    /** Entra object id of the grantor. */
    grantedBy: z.string().min(1),
    grantedByRole: hrRoleSchema,
    grantedAt: z.string().datetime(),
    /** Null is legal in the schema but an admin UI should not offer it lightly. */
    expiresAt: z.string().datetime().nullable().default(null),
    revokedAt: z.string().datetime().nullable().default(null),
    revokedBy: z.string().min(1).nullable().default(null),
});
//# sourceMappingURL=vocabulary.js.map